/**
 * Image Editor Utilities
 * Functions for rotating and cropping images
 */

/**
 * Rotate image by 90 degrees
 * @param {File|Blob} imageFile - Original image file
 * @param {number} currentRotation - Current rotation in degrees (0, 90, 180, 270)
 * @param {number} rotationDirection - Direction: 1 for clockwise (right), -1 for counterclockwise (left)
 * @returns {Promise<Blob>} - Rotated image as Blob
 */
export const rotateImage = (imageFile, currentRotation = 0, rotationDirection = 1) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new rotation
        const newRotation = (currentRotation + (rotationDirection * 90)) % 360;
        const normalizedRotation = newRotation < 0 ? newRotation + 360 : newRotation;
        
        // Swap width/height if rotation is 90 or 270
        if (normalizedRotation === 90 || normalizedRotation === 270) {
          canvas.width = img.height;
          canvas.height = img.width;
        } else {
          canvas.width = img.width;
          canvas.height = img.height;
        }
        
        // Translate and rotate
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate((normalizedRotation * Math.PI) / 180);
        ctx.translate(-img.width / 2, -img.height / 2);
        
        // Draw image
        ctx.drawImage(img, 0, 0);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Image rotation failed'));
            }
          },
          imageFile.type || 'image/jpeg',
          0.95 // Quality
        );
      };
      img.onerror = (error) => reject(new Error('Failed to load image: ' + error));
      img.src = e.target.result;
    };
    reader.onerror = (error) => reject(new Error('Failed to read file: ' + error));
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Crop image based on crop area
 * Note: Crop area coordinates are relative to the displayed image (after rotation is applied via CSS)
 * Since we crop before rotation, we need to account for the rotation when calculating crop coordinates
 * @param {File|Blob} imageFile - Original image file
 * @param {Object} cropArea - Crop area {x, y, width, height} in pixels relative to displayed image
 * @param {number} rotation - Current rotation in degrees (0, 90, 180, 270)
 * @returns {Promise<Blob>} - Cropped image as Blob
 */
export const cropImage = (imageFile, cropArea, rotation = 0) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate scale factor (image might be displayed at different size)
        // For now, assume cropArea is in image pixels (we'll need to pass scale if image is scaled)
        // This is a simplified version - in production, you'd want to calculate the actual scale
        
        // Set canvas size to crop area
        canvas.width = cropArea.width;
        canvas.height = cropArea.height;
        
        // Crop coordinates are relative to the displayed (rotated) image
        // We need to convert them to original image coordinates
        // For simplicity, we'll assume the crop area is already in original image coordinates
        // In a real implementation, you'd need to account for the display scale
        
        // Simple crop: just extract the region
        ctx.drawImage(
          img,
          cropArea.x,
          cropArea.y,
          cropArea.width,
          cropArea.height,
          0,
          0,
          cropArea.width,
          cropArea.height
        );
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Image cropping failed'));
            }
          },
          imageFile.type || 'image/jpeg',
          0.95 // Quality
        );
      };
      img.onerror = (error) => reject(new Error('Failed to load image: ' + error));
      img.src = e.target.result;
    };
    reader.onerror = (error) => reject(new Error('Failed to read file: ' + error));
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Create a preview URL from file/blob
 * @param {File|Blob} file - Image file
 * @returns {Promise<string>} - Object URL for preview
 */
export const createPreviewUrl = (file) => {
  return new Promise((resolve, reject) => {
    if (file instanceof File || file instanceof Blob) {
      const url = URL.createObjectURL(file);
      resolve(url);
    } else {
      reject(new Error('Invalid file type'));
    }
  });
};

/**
 * Revoke preview URL to free memory
 * @param {string} url - Object URL to revoke
 */
export const revokePreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Crop image to circular shape
 * @param {File|Blob} imageFile - Original image file
 * @param {Object} cropArea - Crop area {x, y, width, height} in pixels (should be square for circle)
 * @returns {Promise<Blob>} - Cropped circular image as Blob
 */
export const cropImageToCircle = (imageFile, cropArea) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Use the smaller dimension to ensure it's a perfect circle
        const size = Math.min(cropArea.width, cropArea.height);
        canvas.width = size;
        canvas.height = size;
        
        // Create circular clipping path
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
        ctx.clip();
        
        // Draw the cropped portion of the image
        ctx.drawImage(
          img,
          cropArea.x,
          cropArea.y,
          size,
          size,
          0,
          0,
          size,
          size
        );
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Circular image cropping failed'));
            }
          },
          imageFile.type || 'image/png', // Use PNG to preserve transparency
          0.95 // Quality
        );
      };
      img.onerror = (error) => reject(new Error('Failed to load image: ' + error));
      img.src = e.target.result;
    };
    reader.onerror = (error) => reject(new Error('Failed to read file: ' + error));
    reader.readAsDataURL(imageFile);
  });
};

/**
 * Convert blob to File object
 * @param {Blob} blob - Blob to convert
 * @param {string} fileName - Original file name
 * @returns {File} - File object
 */
export const blobToFile = (blob, fileName) => {
  return new File([blob], fileName, { type: blob.type });
};

